import React, { useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  AccountBalanceWallet,
  AdminPanelSettings,
  Numbers,
  Refresh,
  Search,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { fetchAdminPayouts, fetchAdminPayoutsSummary } from '../hooks/hooks';

const PAGE_SIZE = 50;

const formatNum = (val) => Number(val || 0).toLocaleString();
const formatMoney = (val) => Number(val || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const StatCard = ({ icon: Icon, label, value, color, cardBg, borderClr }) => (
  <Card elevation={0} sx={{ border: `1px solid ${borderClr}`, bgcolor: cardBg, borderRadius: '12px', height: '100%' }}>
    <CardContent sx={{ py: 1.75, '&:last-child': { pb: 1.75 } }}>
      <Stack direction="row" alignItems="center" gap={1} mb={0.75}>
        <Icon sx={{ fontSize: 18, color: color || '#0BA480' }} />
        <Typography variant="body2" color="text.secondary" noWrap>
          {label}
        </Typography>
      </Stack>
      <Typography variant="h5" fontWeight={700}>
        {value}
      </Typography>
    </CardContent>
  </Card>
);

const AdminPayouts = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const cardBg = isDark ? 'rgba(255,255,255,0.03)' : '#fff';
  const borderClr = isDark ? 'rgba(255,255,255,0.09)' : '#E2E8F0';

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);

  const {
    data: summary,
    isLoading: summaryLoading,
    error: summaryError,
    refetch: refetchSummary,
  } = useQuery({
    queryKey: ['adminPayoutsSummary'],
    queryFn: fetchAdminPayoutsSummary,
    refetchOnWindowFocus: false,
    staleTime: 60 * 1000,
  });

  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['adminPayouts', page, search],
    queryFn: () => fetchAdminPayouts({
      page: page + 1,
      ...(search ? { search } : {}),
    }),
    refetchOnWindowFocus: false,
    staleTime: 30 * 1000,
    placeholderData: keepPreviousData,
  });

  const rows = Array.isArray(data?.results) ? data.results : [];
  const count = data?.count || 0;

  const statCards = summaryLoading ? [] : [
    { icon: AccountBalanceWallet, label: 'Total Payout', value: formatMoney(summary?.total_payout) },
    { icon: Numbers, label: 'Total Payouts', value: formatNum(summary?.total_count), color: '#3B82F6' },
  ];

  const handleRefreshAll = () => {
    refetchSummary();
    refetch();
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={3}>
        <Box>
          <Stack direction="row" alignItems="center" gap={1} mb={0.5}>
            <AdminPanelSettings sx={{ color: '#0BA480', fontSize: 28 }} />
            <Typography variant="h5" fontWeight={700}>
              Payouts
            </Typography>
            <Chip label="Admin" size="small" color="success" sx={{ fontWeight: 600 }} />
          </Stack>
          <Typography color="text.secondary" fontSize={14}>
            Wallet-to-bank transfers across all users.
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={handleRefreshAll}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </Stack>

      {(summaryError || error) && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }}>
          Failed to load some payouts data. Please try refreshing.
        </Alert>
      )}

      {summaryLoading ? (
        <Typography color="text.secondary" sx={{ mb: 3 }}>Loading summary...</Typography>
      ) : (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {statCards.map((s) => (
            <Grid item xs={6} sm={3} key={s.label}>
              <StatCard {...s} cardBg={cardBg} borderClr={borderClr} />
            </Grid>
          ))}
        </Grid>
      )}

      <Card elevation={0} sx={{ border: `1px solid ${borderClr}`, bgcolor: cardBg, borderRadius: '12px' }}>
        <Box sx={{ px: 2, pt: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5} flexWrap="wrap" gap={1.5}>
            <Typography variant="subtitle1" fontWeight={700}>
              All Payouts {isLoading ? '' : `(${formatNum(count)})`}
            </Typography>
            <TextField
              size="small"
              placeholder="Search user, recipient, ref..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              InputProps={{ startAdornment: <Search sx={{ fontSize: 18, mr: 0.75, color: 'text.disabled' }} /> }}
              sx={{ minWidth: 240 }}
            />
          </Stack>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#F8FAFC' }}>
                <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>User</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 13 }} align="right">Amount</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Currency</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Recipient</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Account No</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Reference</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                    <Typography color="text.secondary">Loading payouts...</Typography>
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                    <Typography color="text.secondary">No payouts found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((r) => (
                  <TableRow key={r.id} sx={{ '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'action.hover' } }}>
                    <TableCell>
                      <Typography fontSize={12.5}>
                        {r.created_at ? format(new Date(r.created_at), 'MMM d, yyyy HH:mm') : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack>
                        <Typography fontSize={13} fontWeight={500}>{r.username || '—'}</Typography>
                        <Typography fontFamily="monospace" fontSize={11} color="text.secondary">{r.account_id || '—'}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontSize={13} fontWeight={600}>{formatMoney(r.amount)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography fontSize={13}>{r.currencyCode || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography fontSize={13}>{r.recipientNames || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography fontFamily="monospace" fontSize={12.5}>{r.accountNo || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={r.statusCode || 'unknown'}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: 11 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography fontFamily="monospace" fontSize={12}>{r.transactionRef || '—'}</Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {count > 0 && (
          <TablePagination
            component="div"
            count={count}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={PAGE_SIZE}
            rowsPerPageOptions={[PAGE_SIZE]}
          />
        )}
      </Card>
    </Container>
  );
};

export default AdminPayouts;
